// Utils
import { getTimestamp } from '../../utils/timeUtil';
import { asObject, isEmpty } from '../../utils/immutableHelper';
import rawHasText from '../../utils/editor/rawUtils/rawHasText';
import { addRestoreLocationForMovesToTrash, getTrashLocation } from './elementLocationUtils';
import {
    getCaption,
    getElementId,
    getElementLocation,
    getElementType,
    getFileProp,
    getImageProp,
    getLinkUrl,
    getLocationParentId,
    getShowCaption,
    getTextContent,
    getTitle,
} from './elementPropertyUtils';

// Constants
import { CARD_TYPE } from '../../cards/cardConstants';
import { LINE_TYPE } from '../../lines/lineConstants';
import { COLUMN_DEFAULT_TITLE, COLUMN_TYPE } from '../../columns/columnConstants';
import { TASK_TYPE } from '../../tasks/tasksConstants';
import { BOARD_DEFAULT_TITLE, BOARD_TYPE } from '../../boards/boardConstants';
import { IMAGE_TYPE } from '../../images/imageConstants';
import { LINK_TYPE } from '../../links/linkConstants';
import { COMMENT_THREAD_TYPE } from '../../comments/commentConstants';
import { TASK_LIST_TYPE } from '../../taskLists/taskListConstants';
import { ALIAS_TYPE } from '../../alias/aliasConstants';
import { FILE_TYPE } from '../../files/fileConstants';
import { DOCUMENT_TYPE } from '../../documents/documentConstants';
import { ANNOTATION_TYPE } from '../../drawings/annotations/annotationConstants';
import { COLOR_SWATCH_TYPE } from '../../colorSwatches/colorSwatchesConstants';

// Types
import { MNElement } from '../elementModelTypes';

const elementHasCaption = (el: MNElement) => {
    const showCaption = getShowCaption(el);
    if (!showCaption) return false;

    const caption = getCaption(el);
    return rawHasText(caption);
};

export const cardOrDocumentHasContent = (el: MNElement) => {
    const textContent = getTextContent(el);

    if (rawHasText(textContent)) return true;

    return elementHasCaption(el);
};

export const boardHasContent = (el: MNElement) => {
    const title = getTitle(el);
    return !isEmpty(title) && title !== BOARD_DEFAULT_TITLE;
};

export const columnHasContent = (el: MNElement) => {
    const title = getTitle(el);
    return !isEmpty(title) && title !== COLUMN_DEFAULT_TITLE;
};

export const imageHasContent = (el: MNElement) => {
    const image = getImageProp(el);

    if (!isEmpty(image)) return true;

    return elementHasCaption(el);
};

export const linkHasContent = (el: MNElement) => {
    const url = getLinkUrl(el);
    return !!url;
};

export const taskHasContent = (el: MNElement) => {
    const textContent = getTextContent(el);
    return rawHasText(textContent);
};

export const fileHasContent = (el: MNElement) => {
    const file = getFileProp(el);
    return !!file;
};

/**
 * This is used to determine if the specific element has content that should be shown in the trash.
 * Elements with children will still be shown even if they aren't interesting themselves - this is handled
 * by the trash selector.
 *
 * NOTE:  This could also use the "element registration" functionality - allowing an "isEmpty" function
 *  to be registered for each element type.  So far this appears to have caused more issues in terms of
 *  code cleanliness than it's solved, so I've opted to avoid that in this case.
 *  It may prove necessary if we ever have dynamic element types, or as an enforcement of implementation
 *  for new element types.
 */
export const elementHasInterestingContentForTrash = (el: MNElement) => {
    const elementType = getElementType(el);

    switch (elementType) {
        case CARD_TYPE:
        case DOCUMENT_TYPE:
            return cardOrDocumentHasContent(el);
        case COLUMN_TYPE:
            return columnHasContent(el);
        case IMAGE_TYPE:
            return imageHasContent(el);
        case LINK_TYPE:
            return linkHasContent(el);
        case BOARD_TYPE:
            return boardHasContent(el);
        // If a single task exists in the trash (without a containing task list, then it won't be shown)
        case ALIAS_TYPE:
        case TASK_LIST_TYPE:
        case TASK_TYPE:
        case COMMENT_THREAD_TYPE:
        case LINE_TYPE:
        case ANNOTATION_TYPE:
            return false;
        case FILE_TYPE:
        default:
            return true;
    }
};

export const getTrashMoveAction = (el: MNElement) =>
    addRestoreLocationForMovesToTrash({
        id: getElementId(el),
        location: getTrashLocation({
            currentBoardId: getLocationParentId(el),
            addedDate: getTimestamp(),
        }),
        from: asObject(getElementLocation(el)),
    });

/**
 * Determines if the element is suitable for permanent deletion based on its content alone.
 */
export const canContentBePermanentlyDeleted = (el: MNElement) => {
    const elementType = getElementType(el);

    switch (elementType) {
        case CARD_TYPE:
        case DOCUMENT_TYPE:
            return !cardOrDocumentHasContent(el);
        case IMAGE_TYPE:
            return !imageHasContent(el);
        case LINK_TYPE:
            return !linkHasContent(el);
        case FILE_TYPE:
            return !fileHasContent(el);
        case COLOR_SWATCH_TYPE:
        case LINE_TYPE:
            return true;
        default:
            return false;
    }
};
